import { useMemo } from 'react'
import {
  computeCodeFrequencyReport,
  type CodeFrequencyReport,
} from '../../../../../governance'
// FG4 §Fase B — re-exported so OverviewDashboard can type its prop.
export type { ShadowImpactReport } from '../../../../../governance'
import { ALL_QUESTIONS_CORE, MODULES } from '../../../../../content'
import { VISUAL_AID_TYPES } from '../../../../../visuals'
import { QUESTION_TYPE_REGISTRY } from '../../../../../content/questions/questionTypeRegistry'
import { usePipelineRun } from './usePipelineRun'
import {
  buildUnifiedRows,
  computeGovernanceStats,
  computeModuleAndFamilyHealth,
  selectTopBlockers,
  computeFamilyTriage,
  computeAssessmentPoolHealth,
  type UnifiedQuestionRow,
  type GovernanceStats,
  type ModuleHealth,
  type FamilyHealth,
  type FamilyTriageItem,
  type AssessmentPoolHealthReport,
  type AssessmentPoolSliceFinding,
  type AssessmentPoolFeasibilityFinding,
} from './derivations'

// Re-export view-model types so existing consumers
// (`OverviewDashboard`, `QuestionsTab`, etc.) keep importing from this barrel.
export type {
  UnifiedQuestionRow,
  GovernanceStats,
  ModuleHealth,
  FamilyHealth,
  FamilyTriageItem,
  AssessmentPoolHealthReport,
  AssessmentPoolSliceFinding,
  AssessmentPoolFeasibilityFinding,
}

export interface SystemStats {
  visualTypeCount: number
  questionTypeCount: number
  familyCount: number
  moduleCount: number
}

/**
 * useGovernanceData — orchestration hook for the governance admin views.
 *
 * R1 (refactor): the previous 450-line monolith is now a thin composition
 * over `usePipelineRun` (lifecycle) plus pure derivation utilities in
 * `./derivations`. The external API (return shape, types) is unchanged so
 * `GovernancePage` and the tabs continue to work without modification.
 *
 * Responsibilities of this hook:
 *  1. Drive the pipeline run lifecycle (delegated to `usePipelineRun`)
 *  2. Memoize each view-model derivation against `batchResult`
 *  3. Surface read-only system metadata (counts of visuals, types, modules)
 */
export function useGovernanceData() {
  const {
    isRunning,
    lastRun,
    runDuration,
    batchResult,
    shadowImpactReport,
    revalidate,
  } = usePipelineRun()
  const totalQuestions = ALL_QUESTIONS_CORE.length

  const rows = useMemo<UnifiedQuestionRow[]>(
    () => (batchResult ? buildUnifiedRows(batchResult) : []),
    [batchResult],
  )

  const stats = useMemo<GovernanceStats>(
    () => computeGovernanceStats(batchResult, totalQuestions),
    [batchResult, totalQuestions],
  )

  const {
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    uniqueFamiliesCount,
  } = useMemo(() => computeModuleAndFamilyHealth(batchResult), [batchResult])

  const topBlockers = useMemo<UnifiedQuestionRow[]>(
    () => selectTopBlockers(rows),
    [rows],
  )

  const familyTriage = useMemo<FamilyTriageItem[]>(
    () => computeFamilyTriage(rows),
    [rows],
  )

  const assessmentPoolHealth = useMemo<AssessmentPoolHealthReport>(
    () => computeAssessmentPoolHealth(batchResult),
    [batchResult],
  )

  const codeFrequencyReport = useMemo<CodeFrequencyReport | null>(
    () =>
      batchResult
        ? computeCodeFrequencyReport(batchResult, { topN: 10 })
        : null,
    [batchResult],
  )

  const systemStats: SystemStats = useMemo(
    () => ({
      visualTypeCount: VISUAL_AID_TYPES.length,
      questionTypeCount: Object.keys(QUESTION_TYPE_REGISTRY).length,
      familyCount: uniqueFamiliesCount,
      moduleCount: MODULES.length,
    }),
    [uniqueFamiliesCount],
  )

  return {
    rows,
    stats,
    systemStats,
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    topBlockers,
    familyTriage,
    assessmentPoolHealth,
    pipelineIssueCounts: batchResult?.issuesByDomain || null,
    codeFrequencyReport,
    shadowImpactReport,
    isRunning,
    lastRun,
    runDuration,
    batchResult,
    revalidate,
  }
}
